import { useElementSize } from "@mantine/hooks";
import { File as FileEntity } from "@server/entities";
import { Skeleton } from "@synqup/ui";
import { useDrag } from "@use-gesture/react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useBufferPeaks } from "./useBufferPeaks";
import { useDecodedAudio } from "./useDecodedAudio";
import { PeaksStatus, usePeaks } from "./usePeaks";
import { buildEnvelopePath, timeToX, xToTime } from "./waveform-geometry";

const WAVEFORM_COLOUR = "#B7C1F2";
// indigo-700 -> rose-700, the gradient the old player card carried.
const BACKGROUND = "linear-gradient(90deg, #4338CA, #BE123C)";

// No single colour contrasts with both the mid-tone gradient and the pale
// waveform — nothing in the style guide scores above 1.99 worst-case, so
// each marker is strong in one place and soft in the other:
//   white playhead  -> 7.90/6.29:1 on the gradient, 1.76:1 on the waveform
//   black seek      -> 8.92:1 on the waveform, 1.99/2.50:1 on the gradient
// They sit 15.7:1 apart from each other, so they can never be confused.
const PLAYHEAD_COLOUR = "#FFFFFF";
const SEEK_COLOUR = "#232324";

// Optional thin outline behind each marker, for crispness everywhere at the
// cost of a little visual weight. Flip to true to compare.
const MARKER_CASING = false;
const CASING_COLOUR = "#B7C1F2";

export interface WaveformDragSeekInfo {
  /** Pointer position mapped through xToTime, this frame. */
  time: number;
  /** Pointer position mapped through xToTime, at drag start. */
  startTime: number;
  /** Pixels moved since drag start (signed — negative is leftward). */
  movementX: number;
  /** Whether the pointer is still held down. */
  down: boolean;
}

interface WaveformLaneProps {
  /**
   * The uploaded file, whose precomputed peaks (.dat) the lane prefers to
   * draw from. Optional because the create-project page has audio in the
   * form before it has a File entity — see `buffer`.
   */
  file?: FileEntity;
  /**
   * A locally-decoded AudioBuffer to draw from when `file` has no usable
   * peaks yet. Covers two cases: the create-project page (nothing uploaded,
   * so no peaks will ever exist for this audio) and the window after an
   * upload while the peaks Lambda is still running. Server peaks take
   * precedence as soon as they are ready, so passing both is the norm.
   * Pass `null` while still decoding it; omit the prop entirely and the
   * lane decodes `file` itself whenever its peaks are unavailable.
   */
  buffer?: AudioBuffer | null;
  currentTime: number;
  /**
   * Whether the transport is currently playing. Only affects smoothing:
   * while true the lane advances the playhead itself between the ~5/sec
   * updates `currentTime` arrives at, instead of stepping. Omit it and the
   * playhead simply follows `currentTime`, as it did before.
   */
  playing?: boolean;
  duration: number;
  height?: number;
  onSeek: (time: number) => void;
  /**
   * Reports every drag update instead of the lane seeking by itself. The
   * three surfaces this component serves disagree about what a drag means
   * (plain scrub vs. draw-a-selection-region vs. scrub-only-if-selected), so
   * the lane exposes the gesture and lets each surface decide — it must not
   * bake in one surface's interpretation. Omit this prop to get the simple
   * fallback: `onSeek(time)` called continuously while the pointer is held.
   */
  onDragSeek?: (info: WaveformDragSeekInfo) => void;
  /**
   * Reports the lane's own measured pixel width whenever it changes.
   * Consumers that draw their own overlays (markup pins, selection
   * regions, progress fills) as `children` must position them against
   * this exact number rather than a parallel measurement of their own —
   * the lane has no margin/padding of its own, so its measured box IS the
   * waveform's pixel width, but a second `useElementSize` on an ancestor
   * element is not guaranteed to agree with it (and historically didn't:
   * see the `mixdown_canvas` margin bug this was added to close off).
   */
  onMeasure?: (width: number) => void;
  children?: React.ReactNode;
}

/**
 * Draws the waveform envelope, the playhead and the hover seek cursor for a
 * single audio file. This is the shared drawing surface for the project
 * mixdown player, the voice-memo player and the mixdown-compare view — each
 * of those keeps its own playback transport and renders this underneath it.
 *
 * `resample(width)` from usePeaks returns `null` in two distinct cases: no
 * data has loaded yet (reflected by `status`, not this), and "cannot draw at
 * this width" (the decoded peaks are too coarse to resample up to the
 * current lane width). Both a `null` resample while `status === "ready"` and
 * `status === "failed"` render the same flat centre line rather than a
 * broken/empty path — the lane stays visibly present and clickable either
 * way.
 */
export const WaveformLane: React.FC<WaveformLaneProps> = ({
  file,
  buffer,
  currentTime,
  playing = false,
  duration,
  height = 92,
  onSeek,
  onDragSeek,
  onMeasure,
  children,
}) => {
  const { ref: sizeRef, width } = useElementSize<HTMLDivElement>();
  const { status: peaksStatus, resample: resamplePeaks } = usePeaks(file);

  // When the page doesn't supply a buffer at all (the audio-file page and
  // the compare view never pass one), decode the file ourselves when its
  // peaks won't come. `null` is different from omitted: it means the page
  // is decoding its own copy (the project page), so we wait for that rather
  // than downloading the same track twice. Decode when peaks are:
  // pending, failed, never generated (files that predate peaks have no
  // status at all), or the .dat itself failed to fetch. Not while ready
  // peaks are merely in flight — that would download the whole track every
  // time, which is exactly what peaks exist to avoid.
  const peaksUnavailable =
    file?.peaksStatus !== "ready" || peaksStatus === "failed";
  const decodeUrl =
    buffer === undefined && peaksUnavailable
      ? file?.url || undefined
      : undefined;
  const decoded = useDecodedAudio(decodeUrl);
  const resampleBuffer = useBufferPeaks(buffer ?? decoded.buffer);

  // Precedence: precomputed peaks whenever they're ready (cheap), otherwise
  // a locally-decoded buffer, whether the page supplied it or we decoded it.
  // Falling back rather than choosing once means a lane that starts on a
  // decoded buffer upgrades itself silently when the peaks Lambda finishes.
  // While decoding the lane shows its skeleton, and if decoding fails too it
  // drops to the flat line, which unlike the skeleton is still seekable.
  const usingBuffer = peaksStatus !== "ready" && !!resampleBuffer;
  let status: PeaksStatus = peaksStatus;
  if (usingBuffer) status = "ready";
  else if (decoded.status === "loading") status = "loading";
  else if (decoded.status === "failed") status = "failed";
  const resample = usingBuffer ? resampleBuffer : resamplePeaks;

  const [seekX, setSeekX] = useState<number | null>(null);
  const laneRef = useRef<HTMLDivElement | null>(null);

  // Let consumers position their own overlays (markup pins, selection
  // regions, progress fills) against the exact width this component is
  // drawing against, instead of a parallel measurement that can drift out
  // of sync (e.g. an ancestor element with different margin/padding).
  useEffect(() => {
    onMeasure?.(width);
  }, [width]);

  // The measured element and the hit-tested element must be the SAME DOM
  // node. `width` (below) drives drawing/geometry off `sizeRef`'s box, and
  // `positionFromEvent` reads offsets off `laneRef`'s box — if those ever
  // pointed at two different elements (e.g. an outer wrapper vs. an inner
  // div), padding/border/margin added to just one of them later would make
  // `width` and the click rect's left edge disagree, silently seeking to
  // the wrong time on every consumer surface. Merging them into one ref
  // callback makes that impossible rather than merely coincidentally true.
  const setLaneRef = useCallback(
    (node: HTMLDivElement | null) => {
      laneRef.current = node;
      sizeRef.current = node as HTMLDivElement;
    },
    [sizeRef]
  );

  const path = useMemo(() => {
    if (status !== "ready" || width <= 0) return "";
    const peaks = resample(width);
    if (!peaks) return "";
    return buildEnvelopePath(peaks.min, peaks.max, width, height);
  }, [status, width, height, resample]);

  const playheadRef = useRef<HTMLDivElement | null>(null);
  const casingRef = useRef<HTMLDivElement | null>(null);

  // The playhead is positioned imperatively rather than from render output.
  // Its position changes continuously during playback, and routing that
  // through React state would re-render this component — plus every overlay
  // a consumer hands us as `children` (markup pins, selection regions) — on
  // every update. Writing the node's style directly keeps the motion off
  // the React path; using `transform` rather than `left` keeps it off the
  // layout path too, so the browser only has to recomposite.
  const positionPlayhead = useCallback(
    (time: number) => {
      const x = Math.min(
        timeToX(time, duration, width),
        Math.max(width - 2, 0)
      );
      if (playheadRef.current) {
        playheadRef.current.style.transform = `translateX(${x}px)`;
      }
      if (casingRef.current) {
        casingRef.current.style.transform = `translateX(${x - 1}px)`;
      }
    },
    [duration, width]
  );

  // Smooth playback motion without easing anything.
  //
  // `currentTime` arrives from the transport about five times a second, so
  // driving the playhead straight off it makes it step rather than glide.
  // While playing, this advances it from the wall clock between those
  // updates and re-anchors to the real value each time one lands, so it
  // reports true position and cannot drift.
  //
  // This is NOT interpolation between two positions, and that distinction
  // is the whole design: a seek changes `currentTime`, which re-anchors,
  // and the synchronous write below puts the playhead on the new spot
  // before the browser paints — it never travels there. That is what keeps
  // requirement 5 ("playhead moves to a clicked position in a single
  // frame") intact. A CSS transition could not: it cannot tell a seek from
  // playback, so it would slide on both.
  useLayoutEffect(() => {
    positionPlayhead(currentTime);

    if (!playing) return;

    const anchorTime = currentTime;
    const anchorAt = performance.now();
    let frame = 0;

    const tick = () => {
      const predicted = anchorTime + (performance.now() - anchorAt) / 1000;
      positionPlayhead(
        duration > 0 ? Math.min(predicted, duration) : predicted
      );
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // `status` is a dependency because the playhead node only exists once
    // the lane is past "loading" — without it the first position write
    // would land on a null ref and the playhead would sit at zero until the
    // next transport update.
  }, [currentTime, playing, duration, status, positionPlayhead]);

  const positionFromEvent = (clientX: number) => {
    const rect = laneRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return clientX - rect.left;
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const x = positionFromEvent(event.clientX);
    if (x === null || width <= 0) return;
    const time = xToTime(x, width, duration);
    if (onDragSeek) {
      onDragSeek({ time, startTime: time, movementX: 0, down: false });
    } else {
      onSeek(time);
    }
  };

  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const x = positionFromEvent(event.clientX);
    setSeekX(x);
  };

  // How the drag/click split avoids a double-seek (verified against
  // @use-gesture/react's actual emitted frames, not assumed from its
  // types): with `filterTaps: true`, a gesture that resolves as a tap
  // (movement under the ~3px threshold) is force-emitted as a *single*
  // frame at release, with `tap: true` — and, on that frame, `first`/
  // `last` come back `undefined`, not `false`, because the normal
  // per-frame bookkeeping that sets them never ran. So `tap` is the only
  // field that reliably identifies it; checking `first`/`last` instead
  // would silently miss it. A genuine drag never sets `tap` on any of its
  // frames (by the time one crosses the threshold and starts emitting, its
  // cumulative distance already exceeds it), so skipping on `tap` alone
  // never touches continuous scrubbing.
  //
  // That's the "don't seek here" half. The other half — stopping the
  // browser's trailing click from ALSO seeking after a genuine drag — is
  // `filterTaps` itself: use-gesture calls `preventDefault`/
  // `stopPropagation` on that click from a capture-phase listener whenever
  // `!tap`. That's native event propagation, not a timing assumption this
  // component makes on its own, so our bubble-phase onClick above simply
  // never runs for a real drag's trailing click — confirmed by dispatching
  // the sequence in jsdom rather than assumed.
  const bindDrag = useDrag(
    ({ xy, initial, movement, down, tap, event }) => {
      if (width <= 0) return;
      const rect = laneRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = xy[0] - rect.left;
      const startX = initial[0] - rect.left;
      const time = xToTime(x, width, duration);
      const startTime = xToTime(startX, width, duration);

      // Touch devices must never show the hover/scrub seek cursor — only
      // update it here for mouse/pen, matching the existing onMouseMove
      // gate.
      const pointerType = (event as PointerEvent).pointerType;
      if (pointerType !== "touch") setSeekX(x);

      if (tap) return;

      if (onDragSeek) {
        onDragSeek({ time, startTime, movementX: movement[0], down });
      } else if (down) {
        onSeek(time);
      }
    },
    { filterTaps: true }
  );

  return (
    <div
      style={{ background: BACKGROUND, borderRadius: 6, overflow: "hidden" }}
      data-testid="waveform-lane"
    >
      <div
        ref={setLaneRef}
        // `group/lane` lets consumers reveal in-lane controls on hover with
        // `group-hover/lane:` alone — no React state, so no re-render per
        // enter/leave and the fade stays on the compositor. Named rather
        // than a bare `group` so a consumer's own nested groups (or a
        // future one inside `children`) can't be captured by this element.
        className="group/lane"
        style={{ position: "relative", height, cursor: "pointer" }}
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={() => setSeekX(null)}
        {...bindDrag()}
      >
        {status === "loading" && (
          <Skeleton visible height={height} data-testid="waveform-loading" />
        )}

        {status !== "loading" && width > 0 && (
          <svg
            width={width}
            height={height}
            style={{ display: "block", position: "absolute", inset: 0 }}
          >
            {status === "ready" && path ? (
              <path d={path} fill={WAVEFORM_COLOUR} />
            ) : (
              <rect
                x={0}
                y={height / 2 - 1}
                width={width}
                height={2}
                fill={WAVEFORM_COLOUR}
                data-testid="waveform-flatline"
              />
            )}
          </svg>
        )}

        {children}

        {status !== "loading" && MARKER_CASING && (
          <div
            ref={casingRef}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: 4,
              height,
              background: CASING_COLOUR,
              pointerEvents: "none",
            }}
          />
        )}

        {status !== "loading" && (
          <div
            ref={playheadRef}
            data-testid="waveform-playhead"
            // `left: 0` is the origin; the actual position is a `transform`
            // written by positionPlayhead above. Do not move this back to a
            // `left` driven by render output — that reintroduces both the
            // per-update layout pass and the React re-render this avoids.
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: 2,
              height,
              background: PLAYHEAD_COLOUR,
              pointerEvents: "none",
            }}
          />
        )}

        {status !== "loading" && seekX !== null && MARKER_CASING && (
          <div
            style={{
              position: "absolute",
              left: seekX - 1,
              top: 0,
              width: 3,
              height,
              background: CASING_COLOUR,
              pointerEvents: "none",
            }}
          />
        )}

        {status !== "loading" && seekX !== null && (
          <div
            data-testid="waveform-seek-cursor"
            style={{
              position: "absolute",
              left: seekX,
              top: 0,
              width: 2,
              height,
              background: SEEK_COLOUR,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </div>
  );
};
